<template>
  <CheckoutHeader :allow-redirection="false">
    <span class="flex w-full justify-between pl-8">
      <RevIllustration
        :alt="i18n(translations.bouyguesIcon)"
        :height="24"
        src="/img/bouygues/bouyguesLogoSquare.svg"
        :width="24"
      />
      <ClientOnly>
        <Timer v-if="magicLink" :expiration-time="magicLink.expiresAt" />
      </ClientOnly>
    </span>
  </CheckoutHeader>

  <InStoreBouyguesTelecom
    v-if="magicLink"
    :cartSelectionId="magicLink.cartSelectionId"
    :customerDetails="magicLink.customerDetails"
    :listingGrade="magicLink.listingGrade"
    :listingPk="magicLink.listingPk"
    :listingPrice="magicLink.listingPrice"
    :listingPublicId="magicLink.listingPublicId"
    :offerId="magicLink.mobilePlanOfferId"
    :shopId="magicLink.shopId"
  />

  <div v-else class="py-24">
    <TheMainContent v-if="magicLinkError">
      <ExpiredError
        v-if="isExpired(magicLinkError)"
        :magicLinkError
        :magicLinkId
      />
      <UsedError v-else-if="isUsed(magicLinkError)" />
      <GenericError v-else :status="magicLinkError.statusCode" />
    </TheMainContent>
  </div>

  <div id="toast-wrapper" class="fixed z-50">
    <TheToast to="#toast-wrapper" />
  </div>
</template>

<script lang="ts" setup>
import type { NuxtError } from '#app'
import { useRouteParams } from '#imports'
import { onMounted, ref } from 'vue'

import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import TheToast from '@backmarket/nuxt-module-toast/TheToast.vue'
import { RevIllustration } from '@ds/components/Illustration'

import TheMainContent from '~/components/TheMainContent/TheMainContent.vue'
import CheckoutHeader from '~/scopes/checkout/components/CheckoutHeader/CheckoutHeader.vue'

import translations from './BouyguesMagicLinkLayout.translations'
import type { MagicLinkError } from './BouyguesMagicLinkLayout.types'
import ExpiredError from './components/ExpiredError/ExpiredError.vue'
import GenericError from './components/GenericError/GenericError.vue'
import InStoreBouyguesTelecom from './components/InStoreBouyguesTelecom/InStoreBouyguesTelecom.vue'
import Timer from './components/Timer.vue'
import UsedError from './components/UsedError/UsedError.vue'
import { useBouyguesMagicLinkAuthentication } from './composable/useBouyguesMagicLinkAuthentication'

const i18n = useI18n()

const magicLink = ref<MagicLinkResponse | null>(null)
const magicLinkError = ref<NuxtError<MagicLinkError> | null>(null)

const $httpFetch = useHttpFetch()
const routeParams = useRouteParams()
const magicLinkId = routeParams.magicLinkId as string

type MagicLinkResponse =
  ApiSpecs['/api/mobile-plan/in-store/v1/magic-links/:magicLinkId']['GET']['response']

onMounted(async () => {
  try {
    // Make sure the user is authenticated with the expected email
    // If not, they will be redirected to the pre-filled login page
    await useBouyguesMagicLinkAuthentication()

    magicLink.value = await $httpFetch(
      '/api/mobile-plan/in-store/v1/magic-links/:magicLinkId',
      {
        pathParams: { magicLinkId },
      },
    )
  } catch (error) {
    magicLinkError.value = error as NuxtError<MagicLinkError>
  }
})

function isExpired(error: NuxtError<MagicLinkError>): boolean {
  return error.data?.status === 410
}

function isUsed(error: NuxtError<MagicLinkError>): boolean {
  return error.data?.status === 422
}
</script>
